<template>
    <div class="admin-panel">
    <a href="/">back</a>
      <div class="admin-header">
        <h1>Admin Panel</h1>
        
        <button @click="checkDatabase" class="db-test-button">
          Test Database Connection
        </button>
      </div>
      
      <!-- Database Status Display -->
      <div v-if="dbStatus" class="db-status">
        <h3>Database Status:</h3>
        <div v-if="dbStatus.connected">
          <p><strong>Status:</strong> Connected ✅</p>
          <p v-if="dbStatus.mode"><strong>Mode:</strong> {{ dbStatus.mode }}</p>
          <p v-if="dbStatus.dbPath"><strong>Path:</strong> {{ dbStatus.dbPath }}</p>
          <p><strong>Regions:</strong> {{ dbStatus.regionsCount }}</p>
          <p><strong>People:</strong> {{ dbStatus.peopleCount }}</p>
          <p><strong>Timestamp:</strong> {{ dbStatus.timestamp }}</p>
          
          <h4>Sample Data:</h4>
          <div class="sample-data">
            <div v-if="dbStatus.sampleData.regions.length > 0">
              <h5>Regions:</h5>
              <ul>
                <li v-for="region in dbStatus.sampleData.regions" :key="region.id">
                  {{ region.id }}: {{ region.name }}
                </li>
              </ul>
            </div>
            
            <div v-if="dbStatus.sampleData.people.length > 0">
              <h5>People:</h5>
              <ul>
                <li v-for="person in dbStatus.sampleData.people" :key="person.id">
                  {{ person.id }}: {{ person.name }} ({{ person.contentType }})
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div v-else class="error-message">
          <p><strong>Status:</strong> Not Connected ❌</p>
          <p v-if="dbStatus.error"><strong>Error:</strong> {{ dbStatus.error }}</p>
          <p v-if="dbStatus.mode === 'browser'">{{ dbStatus.message }}</p>
        </div>
      </div>
      
      <div class="regions-filter">
        <label for="region-filter">Filter by Region:</label>
        <select id="region-filter" v-model="selectedRegionId">
          <option value="0">All Regions</option>
          <option v-for="region in regions" :key="region.id" :value="region.id">
            {{ region.name }}
          </option>
        </select>
      </div>
      
      <div class="people-table">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Region</th>
              <th>Type</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="person in filteredPeople" :key="person.id">
              <td>{{ person.id }}</td>
              <td>{{ person.name }}</td>
              <td>{{ getRegionName(person.region) }}</td>
              <td>{{ person.contentType }}</td>
              <td>
                <button @click="editPerson(person)">Edit</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Edit Person Modal with transition -->
      <Transition name="modal-fade">
        <div class="edit-modal" v-if="showEditModal">
          <div class="modal-content">
            <button class="close-button" @click="closeEditModal">&times;</button>
            <h2>Edit: {{ editingPerson.name }}</h2>
            
            <div class="form-group">
              <label for="edit-name">Name:</label>
              <input type="text" id="edit-name" v-model="editingPerson.name">
            </div>

            <div class="form-group">
              <label for="edit-location">Location:</label>
              <input type="text" id="edit-location" v-model="editingPerson.location">
            </div>
            
            <div class="form-group">
              <label for="edit-region">Region:</label>
              <select id="edit-region" v-model="editingPerson.region">
                <option v-for="region in regions" :key="region.id" :value="region.id">
                  {{ region.name }}
                </option>
              </select>
            </div>
            
            <div class="form-group">
              <label for="edit-description">Description:</label>
              <textarea id="edit-description" v-model="editingPerson.description"></textarea>
            </div>
            
            <div class="form-group" v-if="editingPerson.contentType === 'message'">
              <label for="edit-message">Message:</label>
              <textarea id="edit-message" v-model="editingPerson.message"></textarea>
            </div>

            <div class="form-group">
                <label>Thumbnail:</label>
                <div class="current-thumbnail">
                <!-- Show file preview if selected, otherwise show current thumbnail -->
                <img v-if="thumbnailPreviewUrl" 
                    :src="thumbnailPreviewUrl" 
                    alt="Thumbnail Preview" 
                    class="thumbnail-preview">
                <img v-else
                    :src="editingPerson.thumbnail || '/thumbnails/placeholder.png'" 
                    alt="Current Thumbnail" 
                    class="thumbnail-preview">
                </div>
                <div class="upload-container">
                <input type="file" id="thumbnail-upload" accept="image/*" @change="handleThumbnailUpload">
                <button @click="uploadThumbnail" :disabled="!selectedThumbnailFile">
                    Upload New Thumbnail
                </button>
                </div>
                <div v-if="thumbnailUploadStatus" class="upload-status">
                {{ thumbnailUploadStatus }}
                </div>
            </div>
            
            <div class="form-group" v-if="editingPerson.contentType === 'video'">
              <label for="edit-video">Current Video: {{ editingPerson.videoUrl }}</label>
              <div class="upload-container">
                <input type="file" id="video-upload" accept="video/*" @change="handleFileUpload">
                <button @click="uploadVideo" :disabled="!selectedFile">Upload New Video</button>
              </div>
              <div v-if="uploadStatus" class="upload-status">{{ uploadStatus }}</div>
            </div>
            
            <div class="button-group">
              <button class="save-button" @click="confirmSave">Save Changes</button>
              <button class="cancel-button" @click="closeEditModal">Cancel</button>
            </div>
          </div>
        </div>
      </Transition>
      
      <!-- Confirmation Modal with transition -->
      <Transition name="confirm-scale">
        <div class="confirmation-modal" v-if="showConfirmation">
          <div class="confirmation-content">
            <h3>Confirm Save</h3>
            <p>Are you sure you want to save these changes?</p>
            <div class="confirmation-buttons">
              <button class="confirm-button" @click="saveChanges">Yes, Save Changes</button>
              <button class="cancel-button" @click="showConfirmation = false">Cancel</button>
            </div>
          </div>
        </div>
      </Transition>
    </div>
</template>

<script>
import dataService from '../services/dataService'

export default {
  name: 'AdminPanel',
  data() {
    return {
      regions: [],
      people: [],
      selectedRegionId: 0,
      showEditModal: false,
      showConfirmation: false,
      editingPerson: null,
      originalPerson: null,
      selectedFile: null,
      uploadStatus: '',
      dbStatus: null,
      saveError: null,
      selectedThumbnailFile: null,
      thumbnailPreviewUrl: null,
      thumbnailUploadStatus: ''
    }
  },
  computed: {
    filteredPeople() {
      if (this.selectedRegionId === 0) {
        return this.people;
      }
      return this.people.filter(person => person.region === this.selectedRegionId);
    }
  },
  methods: {
    getRegionName(regionId) {
      const region = this.regions.find(r => r.id === regionId);
      return region ? region.name : 'Unknown';
    },
    editPerson(person) {
      try {
        // Create deep copies to avoid reference issues
        this.originalPerson = JSON.parse(JSON.stringify(person));
        this.editingPerson = JSON.parse(JSON.stringify(person));
        this.showEditModal = true;
        this.selectedFile = null;
        this.uploadStatus = '';
        this.saveError = null;
      } catch (error) {
        console.error('Error preparing person for editing:', error);
        alert('Error preparing data for editing. Please try again.');
      }
    },
    closeEditModal() {
      this.showEditModal = false;
      this.editingPerson = null;
      this.originalPerson = null;
      this.selectedFile = null;
      this.uploadStatus = '';
      this.saveError = null;
    },
    confirmSave() {
      // Show confirmation dialog
      this.showConfirmation = true;
    },
    async saveChanges() {
      try {
        // Hide confirmation modal
        this.showConfirmation = false;
        
        // Make sure Region ID is a number
        if (typeof this.editingPerson.region === 'string') {
          this.editingPerson.region = parseInt(this.editingPerson.region, 10);
        }
        
        // Create a clean copy for saving
        const personToSave = {
          id: this.editingPerson.id,
          name: this.editingPerson.name,
          location: this.editingPerson.location,
          region: this.editingPerson.region,
          contentType: this.editingPerson.contentType,
          videoUrl: this.editingPerson.videoUrl || null,
          message: this.editingPerson.message || null,
          thumbnail: this.editingPerson.thumbnail,
          description: this.editingPerson.description
        };
        
        console.log('Saving person:', personToSave);
        const success = await dataService.updatePerson(personToSave);
        
        if (success) {
          // Update the local array
          const index = this.people.findIndex(p => p.id === personToSave.id);
          if (index !== -1) {
            this.people[index] = { ...personToSave };
          }
          this.closeEditModal();
          
          // Show success message
          alert('Changes saved successfully!');
        } else {
          this.saveError = 'Failed to save changes';
          alert('Failed to save changes. Please try again.');
        }
      } catch (error) {
        console.error('Error saving changes:', error);
        this.saveError = error.message || 'An unknown error occurred';
        alert(`Error saving changes: ${this.saveError}`);
      }
    },
    handleFileUpload(event) {
      this.selectedFile = event.target.files[0];
    },
    async uploadVideo() {
    if (!this.selectedFile) {
        this.uploadStatus = 'Please select a file';
        return;
    }
    
    this.uploadStatus = 'Uploading...';
    
    try {
        const videoUrl = await dataService.uploadVideo(this.editingPerson.id, this.selectedFile);
        
        if (videoUrl) {
        this.editingPerson.videoUrl = videoUrl;
        this.uploadStatus = 'Upload successful';
        } else {
        this.uploadStatus = 'Upload failed';
        }
    } catch (error) {
        console.error('Error uploading:', error);
        this.uploadStatus = `Error: ${error.message}`;
    }
    },
    handleThumbnailUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      this.selectedThumbnailFile = file;
      this.thumbnailUploadStatus = '';
      
      // Create a preview URL for the selected file
      this.thumbnailPreviewUrl = URL.createObjectURL(file);
    },
    
    async uploadThumbnail() {
      if (!this.selectedThumbnailFile) {
        this.thumbnailUploadStatus = 'Please select an image file';
        return;
      }
      
      this.thumbnailUploadStatus = 'Uploading...';
      
      try {
        const thumbnailUrl = await dataService.uploadThumbnail(
          this.editingPerson.id, 
          this.selectedThumbnailFile
        );
        
        if (thumbnailUrl) {
          this.editingPerson.thumbnail = thumbnailUrl;
          this.thumbnailUploadStatus = 'Upload successful';
          
          // Clean up the object URL to prevent memory leaks
          if (this.thumbnailPreviewUrl) {
            URL.revokeObjectURL(this.thumbnailPreviewUrl);
          }
          this.thumbnailPreviewUrl = null;
          this.selectedThumbnailFile = null;
        } else {
          this.thumbnailUploadStatus = 'Upload failed';
        }
      } catch (error) {
        console.error('Error uploading thumbnail:', error);
        this.thumbnailUploadStatus = `Error: ${error.message}`;
      }
    },
    
    // Add cleanup method
    closeEditModal() {
      // Clean up any preview URLs when closing the modal
      if (this.thumbnailPreviewUrl) {
        URL.revokeObjectURL(this.thumbnailPreviewUrl);
        this.thumbnailPreviewUrl = null;
      }
      
      this.showEditModal = false;
      this.editingPerson = null;
      this.selectedThumbnailFile = null;
      this.thumbnailUploadStatus = '';
    },
    async checkDatabase() {
      try {
        this.dbStatus = null; // Reset status
        
        if (window.dbAPI) {
          this.dbStatus = await dataService.checkDatabaseStatus();
          console.log("Database check successful:", this.dbStatus);
        } else {
          // Running in browser mode
          this.dbStatus = { 
            connected: false,
            mode: "browser", 
            message: "Running in browser mode - database not available" 
          };
        }
      } catch (error) {
        console.error("Database check failed:", error);
        this.dbStatus = { 
          connected: false, 
          error: error.message || 'Unknown error checking database',
          timestamp: new Date().toISOString()
        };
      }
    }
  },
  async mounted() {
    try {
      // Fetch data
      const [regions, people] = await Promise.all([
        dataService.getRegions(),
        dataService.getPeople()
      ]);
      
      this.regions = regions || [];
      this.people = people || [];
      
      console.log(`Loaded ${this.regions.length} regions and ${this.people.length} people`);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error loading data. Please refresh the page and try again.');
    }
  }
}
</script>

<style scoped>
.admin-panel {
  padding: 2rem;
}

.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.db-test-button {
  background: #4CAF50;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.db-test-button:hover {
  background: #45a049;
}

.db-status {
  background: #f5f5f5;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
  overflow-x: auto;
}

.error-message {
  color: #d32f2f;
}

.sample-data {
  margin-top: 10px;
  display: flex;
  gap: 30px;
}

.sample-data h5 {
  margin-bottom: 8px;
}

.sample-data ul {
  padding-left: 20px;
}

.regions-filter {
  margin-bottom: 1.5rem;
}

.regions-filter select {
  padding: 0.5rem;
  border-radius: 4px;
  border: 1px solid var(--border-color);
}

.people-table table {
  width: 100%;
  border-collapse: collapse;
}

.people-table th,
.people-table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid var(--border-color);
}

.people-table th {
  background: var(--light-gray);
}

/* Modal styles with improved visuals */
.edit-modal, .confirmation-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
  backdrop-filter: blur(2px);
}

.confirmation-modal {
  z-index: 1200; /* Higher than edit modal */
}

.modal-content, .confirmation-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 2rem;
  position: relative;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.confirmation-content {
  max-width: 400px;
  padding: 1.5rem;
  text-align: center;
}

/* Form styling */
.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: bold;
  color: var(--text-color, #333);
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 0.75rem;
  border-radius: 4px;
  border: 1px solid #dee2e6;
  font-size: 1rem;
  background: #fff;
}

.form-group textarea {
  min-height: 100px;
  resize: vertical;
}

/* Button styling */
.close-button {
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 1.5rem;
  background: none;
  border: none;
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.close-button:hover {
  transform: scale(1.2);
  opacity: 0.8;
}

.button-group {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
}

.save-button, .confirm-button, .db-test-button {
  background: var(--primary-color, #3498db);
  color: white;
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: transform 0.2s, background-color 0.2s;
}

.cancel-button {
  background: var(--light-gray, #f8f9fa);
  color: var(--text-color, #333);
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: transform 0.2s, background-color 0.2s;
}

.upload-container {
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
}

button:hover {
  transform: translateY(-2px);
}

button:active {
  transform: translateY(1px);
}

/* Transitions */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .modal-content,
.modal-fade-leave-to .modal-content {
  transform: scale(0.9);
}

.confirm-scale-enter-active,
.confirm-scale-leave-active {
  transition: all 0.2s ease;
}

.confirm-scale-enter-from,
.confirm-scale-leave-to {
  opacity: 0;
  transform: scale(0.7);
}
</style>